import type {
  InstructionPromptKind,
  InstructionValidation,
  NodeValidation,
} from '@onefootprint/request-types/dashboard';
import i18next from 'i18next';
import {
  parseInstructionChecklistConfig,
  parseInstructionProcedureConfig,
} from '@/components/block-primitives/contracts/agent-blocks';
import type { State } from './reducer';
import { filterValidationsForBundle } from './reducer';
import { type Bundle, isManagedNode, isTaskNode } from './types';

export type InstructionIssue = { path: string; message: string };
export type InstructionBlockState = {
  nodeId: string;
  promptKind: InstructionPromptKind;
  promptKey?: string;
  index: number;
  kind: string;
  config: string;
  touched: boolean;
  issues: InstructionIssue[] | null;
};
export type VisibleValidationDetail = NodeValidation['details'][number] & { blockTarget?: string };
export type VisibleValidation = Omit<NodeValidation, 'details'> & { details: VisibleValidationDetail[] };

export const instructionTarget = (
  block: Pick<InstructionBlockState, 'nodeId' | 'promptKind' | 'promptKey' | 'index'>,
) => JSON.stringify([block.nodeId, block.promptKind, block.promptKey ?? '', block.index]);

// Track all fences, including ordinary code, so examples inside a longer fence cannot claim an error.
export const instructionFences = (markdown: string) => {
  const fences: { line: number; config: string }[] = [];
  const lines = markdown.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const opening = /^[ \t>]*(?:(?:[-+*]|\d+\.)[ \t]+)?(`{3,}|~{3,})[ \t]*(.*)$/.exec(lines[i]);
    if (!opening) continue;
    const line = i + 1;
    const content: string[] = [];
    for (i++; i < lines.length; i++) {
      const closing = /^[ \t>]*(`{3,}|~{3,})[ \t]*$/.exec(lines[i]);
      if (closing && closing[1][0] === opening[1][0] && closing[1].length >= opening[1].length) break;
      content.push(lines[i]);
    }
    if (/^agent-v1(?!\w)/.test(opening[2])) fences.push({ line, config: content.join('\n') });
  }
  return fences;
};

export const instructionPrompt = (
  bundle: Bundle | null,
  nodeId: string,
  location: Pick<InstructionValidation, 'promptKind' | 'promptKey'>,
) => {
  const node = bundle?.nodes.find(node => node.id === nodeId);
  const task = node && (isManagedNode(node) ? node.task : isTaskNode(node) ? node : undefined);
  if (!task) return undefined;
  if (location.promptKind === 'field') return task.fields.find(field => field.name === location.promptKey)?.prompt;
  if (location.promptKind === 'artifact')
    return task.artifacts.find(artifact => artifact.id === location.promptKey)?.spec;
  return task.prompt;
};

export const instructionIssues = (kind: string, rawConfig: string): InstructionIssue[] | null => {
  const config =
    kind === 'checklist'
      ? parseInstructionChecklistConfig(rawConfig)
      : kind === 'procedure'
        ? parseInstructionProcedureConfig(rawConfig)
        : null;
  if (!config) return null;
  const property = 'items' in config ? 'items' : 'steps';
  const values = 'items' in config ? config.items : config.steps;
  const issues: InstructionIssue[] = [];
  if (values.length === 0) {
    issues.push({
      path: property,
      message:
        property === 'items'
          ? i18next.t($ => $.agentBuilder.instructionBlocks.checklistEmpty, { ns: 'agents' })
          : i18next.t($ => $.agentBuilder.instructionBlocks.procedureEmpty, { ns: 'agents' }),
    });
  }
  const checkFence = (text: string, path: string) => {
    if (/^(?:`{3}|~{3})/.test(text.trimStart()))
      issues.push({ path, message: i18next.t($ => $.agentBuilder.instructionBlocks.codeFence, { ns: 'agents' }) });
  };
  if (config.title != null) checkFence(config.title, 'title');
  values.forEach((text, index) => {
    checkFence(text, `${property}[${index}]`);
  });
  return issues;
};

export const visibleValidations = (state: State, reveal = state.showInstructionErrors): VisibleValidation[] => {
  const mounted = Object.values(state.instructionBlocks);
  const result: VisibleValidation[] = filterValidationsForBundle(state.validations, state.bundle).map(validation => ({
    ...validation,
    details: validation.details.flatMap(detail => {
      const instruction = detail.instruction;
      if (!instruction || !validation.id) return [detail];
      const savedPrompt = instructionPrompt(state.savedBundle, validation.id, instruction);
      const currentPrompt = instructionPrompt(state.bundle, validation.id, instruction);
      const fences = instructionFences(savedPrompt ?? currentPrompt ?? '');
      const savedIndex = fences.findIndex(fence => fence.line === instruction.blockLine);
      const currentFences = instructionFences(currentPrompt ?? '');
      const savedFence = fences[savedIndex];
      let index = savedIndex;
      if (
        savedPrompt !== undefined &&
        currentPrompt !== savedPrompt &&
        savedFence &&
        currentFences[index]?.config !== savedFence.config
      ) {
        const matches = currentFences
          .map((fence, index) => ({ fence, index }))
          .filter(item => item.fence.config === savedFence.config);
        if (matches.length !== 1) return [];
        index = matches[0].index;
      }
      const block = mounted.find(
        block =>
          block.nodeId === validation.id &&
          block.promptKind === instruction.promptKind &&
          block.promptKey === instruction.promptKey &&
          block.index === index,
      );
      // Mounted editors validate their current config, which may be ahead of the last save response.
      if (block?.issues && ['empty_list', 'blank_text', 'line_break', 'code_fence'].includes(instruction.code))
        return [];
      return [
        {
          ...detail,
          message: instruction.message,
          blockTarget: index >= 0 ? instructionTarget({ nodeId: validation.id, ...instruction, index }) : undefined,
        },
      ];
    }),
  }));
  for (const block of mounted) {
    if ((!block.touched && !reveal) || !block.issues?.length) continue;
    let validation = result.find(item => item.id === block.nodeId);
    if (!validation) {
      validation = { id: block.nodeId, details: [] };
      result.push(validation);
    }
    validation.details.push(
      ...block.issues.map(issue => ({
        severity: 'error' as const,
        message: issue.message,
        blockTarget: instructionTarget(block),
      })),
    );
  }
  return result.filter(validation => validation.details.length > 0);
};
